import type { SmartContractVerificationMethod } from 'types/client/contract';

export const CONTRACT_VERIFICATION_KEYS = {
  title: 'contractVerification.title',
  titleFallback: 'pages.verifyAndPublishContract',
  submit: 'contractVerification.submit',
  errors: {
    alreadyVerified: 'contractVerification.errors.alreadyVerified',
    notSmartContract: 'contractVerification.errors.notSmartContract',
    somethingWentWrong: 'contractVerification.errors.somethingWentWrong',
    fieldRequired: 'contractVerification.errors.fieldRequired',
    wrongFileType: 'contractVerification.errors.wrongFileType',
    fileTooBig: 'contractVerification.errors.fileTooBig',
    onlyOneFile: 'contractVerification.errors.onlyOneFile',
    invalidGitHubRepo: 'contractVerification.errors.invalidGitHubRepo',
    gitHubRepoNotFound: 'contractVerification.errors.gitHubRepoNotFound',
    commitHashNotFound: 'contractVerification.errors.commitHashNotFound',
  },
  toast: {
    successTitle: 'contractVerification.toast.successTitle',
    successDescription: 'contractVerification.toast.successDescription',
    errorTitle: 'contractVerification.toast.errorTitle',
    socketError: 'contractVerification.toast.socketError',
    verificationFailed: 'contractVerification.toast.verificationFailed',
  },
} as const;

type TranslateFn = (key: string) => string;

export const getContractVerificationTitle = (t: TranslateFn) => {
  const title = t(CONTRACT_VERIFICATION_KEYS.title);

  return title === CONTRACT_VERIFICATION_KEYS.title ? t(CONTRACT_VERIFICATION_KEYS.titleFallback) : title;
};

export const CONTRACT_VERIFICATION_METHOD_LABEL_KEYS: Record<
  SmartContractVerificationMethod,
  string
> = {
  'flattened-code': 'contractVerification.methodLabels.flattenedCode',
  'standard-input': 'contractVerification.methodLabels.standardInput',
  sourcify: 'contractVerification.methodLabels.sourcify',
  'multi-part': 'contractVerification.methodLabels.multiPart',
  'vyper-code': 'contractVerification.methodLabels.vyperCode',
  'vyper-multi-part': 'contractVerification.methodLabels.vyperMultiPart',
  'vyper-standard-input':
    'contractVerification.methodLabels.vyperStandardInput',
  'solidity-hardhat': 'contractVerification.methodLabels.solidityHardhat',
  'solidity-foundry': 'contractVerification.methodLabels.solidityFoundry',
  'stylus-github-repository':
    'contractVerification.methodLabels.stylusGitHubRepository',
};

export const CONTRACT_VERIFICATION_METHOD_DESC_KEYS: Record<
  SmartContractVerificationMethod,
  string
> = {
  'flattened-code': 'contractVerification.methodDescriptions.flattenedCode',
  'standard-input': 'contractVerification.methodDescriptions.standardInput',
  sourcify: 'contractVerification.methodDescriptions.sourcify',
  'multi-part': 'contractVerification.methodDescriptions.multiPart',
  'vyper-code': 'contractVerification.methodDescriptions.vyperCode',
  'vyper-multi-part': 'contractVerification.methodDescriptions.vyperMultiPart',
  'vyper-standard-input':
    'contractVerification.methodDescriptions.vyperStandardInput',
  'solidity-hardhat': 'contractVerification.methodDescriptions.solidityHardhat',
  'solidity-foundry': 'contractVerification.methodDescriptions.solidityFoundry',
  'stylus-github-repository':
    'contractVerification.methodDescriptions.stylusGitHubRepository',
};

export const CONTRACT_VERIFICATION_METHOD_TITLE_KEYS: Record<
  SmartContractVerificationMethod,
  string
> = {
  'flattened-code': 'contractVerification.methodTitles.flattenedCode',
  'standard-input': 'contractVerification.methodTitles.standardInput',
  sourcify: 'contractVerification.methodTitles.sourcify',
  'multi-part': 'contractVerification.methodTitles.multiPart',
  'vyper-code': 'contractVerification.methodTitles.vyperCode',
  'vyper-multi-part': 'contractVerification.methodTitles.vyperMultiPart',
  'vyper-standard-input':
    'contractVerification.methodTitles.vyperStandardInput',
  'solidity-hardhat': 'contractVerification.methodTitles.solidityHardhat',
  'solidity-foundry': 'contractVerification.methodTitles.solidityFoundry',
  'stylus-github-repository':
    'contractVerification.methodTitles.stylusGitHubRepository',
};

export const CONTRACT_VERIFICATION_FIELD_KEYS = {
  address: {
    label: 'contractVerification.fields.address.label',
    placeholder: 'contractVerification.fields.address.placeholder',
  },
  method: {
    title: 'contractVerification.fields.method.title',
    placeholder: 'contractVerification.fields.method.placeholder',
    popoverTitle: 'contractVerification.fields.method.popoverTitle',
  },
  licenseType: {
    placeholder: 'contractVerification.fields.licenseType.placeholder',
    description: 'contractVerification.fields.licenseType.description',
  },
  compiler: {
    placeholder: 'contractVerification.fields.compiler.placeholder',
    includeNightly: 'contractVerification.fields.compiler.includeNightly',
    descriptionPart1: 'contractVerification.fields.compiler.descriptionPart1',
    descriptionPart2: 'contractVerification.fields.compiler.descriptionPart2',
    descriptionPart3: 'contractVerification.fields.compiler.descriptionPart3',
  },
  evmVersion: {
    placeholder: 'contractVerification.fields.evmVersion.placeholder',
    description: 'contractVerification.fields.evmVersion.description',
    detailsLink: 'contractVerification.fields.evmVersion.detailsLink',
  },
  optimization: {
    label: 'contractVerification.fields.optimization.label',
    placeholder: 'contractVerification.fields.optimization.placeholder',
  },
  code: {
    placeholder: 'contractVerification.fields.code.placeholder',
    hint: 'contractVerification.fields.code.hint',
  },
  autodetectArgs: {
    label: 'contractVerification.fields.autodetectArgs.label',
  },
  constructorArgs: {
    placeholder: 'contractVerification.fields.constructorArgs.placeholder',
    descriptionPart1:
      'contractVerification.fields.constructorArgs.descriptionPart1',
    abiHexLink: 'contractVerification.fields.constructorArgs.abiHexLink',
    descriptionPart2:
      'contractVerification.fields.constructorArgs.descriptionPart2',
    descriptionPart3:
      'contractVerification.fields.constructorArgs.descriptionPart3',
    parsedHereLink:
      'contractVerification.fields.constructorArgs.parsedHereLink',
  },
  libraries: {
    addLabel: 'contractVerification.fields.libraries.addLabel',
    itemLabel: 'contractVerification.fields.libraries.itemLabel',
    deleteAria: 'contractVerification.fields.libraries.deleteAria',
    addAria: 'contractVerification.fields.libraries.addAria',
    namePlaceholder: 'contractVerification.fields.libraries.namePlaceholder',
    nameHint: 'contractVerification.fields.libraries.nameHint',
    addressPlaceholder:
      'contractVerification.fields.libraries.addressPlaceholder',
    addressHint: 'contractVerification.fields.libraries.addressHint',
  },
  contractIndex: {
    placeholder: 'contractVerification.fields.contractIndex.placeholder',
  },
  zkCompiler: {
    placeholder: 'contractVerification.fields.zkCompiler.placeholder',
    description: 'contractVerification.fields.zkCompiler.description',
  },
  commit: {
    placeholder: 'contractVerification.fields.commit.placeholder',
    foundLatestPart1: 'contractVerification.fields.commit.foundLatestPart1',
    foundLatestPart2: 'contractVerification.fields.commit.foundLatestPart2',
    clickHere: 'contractVerification.fields.commit.clickHere',
  },
  gitHubRepo: {
    placeholder: 'contractVerification.fields.gitHubRepo.placeholder',
  },
  isYul: {
    label: 'contractVerification.fields.isYul.label',
  },
  name: {
    placeholder: 'contractVerification.fields.name.placeholder',
    hintPart1: 'contractVerification.fields.name.hintPart1',
    hintPart2: 'contractVerification.fields.name.hintPart2',
    hintPart3: 'contractVerification.fields.name.hintPart3',
  },
  pathPrefix: {
    placeholder: 'contractVerification.fields.pathPrefix.placeholder',
    hint: 'contractVerification.fields.pathPrefix.hint',
  },
  sources: {
    standardInputJsonTitle:
      'contractVerification.fields.sources.standardInputJsonTitle',
    standardInputJsonHint:
      'contractVerification.fields.sources.standardInputJsonHint',
    sourcifyTitle: 'contractVerification.fields.sources.sourcifyTitle',
    sourcifyHint: 'contractVerification.fields.sources.sourcifyHint',
    multiPartTitle: 'contractVerification.fields.sources.multiPartTitle',
    multiPartHint: 'contractVerification.fields.sources.multiPartHint',
    vyperMainTitle: 'contractVerification.fields.sources.vyperMainTitle',
    vyperMainHint: 'contractVerification.fields.sources.vyperMainHint',
    vyperInterfacesTitle:
      'contractVerification.fields.sources.vyperInterfacesTitle',
    vyperInterfacesHintPart1:
      'contractVerification.fields.sources.vyperInterfacesHintPart1',
    vyperInterfacesHintLink:
      'contractVerification.fields.sources.vyperInterfacesHintLink',
    vyperInterfacesHintPart2:
      'contractVerification.fields.sources.vyperInterfacesHintPart2',
    dropFile: 'contractVerification.fields.sources.dropFile',
    dropFiles: 'contractVerification.fields.sources.dropFiles',
  },
} as const;

export const CONTRACT_VERIFICATION_TUTORIAL_KEYS = {
  hardhatPart1: 'contractVerification.tutorial.hardhatPart1',
  hardhatLink: 'contractVerification.tutorial.hardhatLink',
  foundryPart1: 'contractVerification.tutorial.foundryPart1',
  foundryLink: 'contractVerification.tutorial.foundryLink',
} as const;
